import React, { useState, useEffect, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Smile, Meh, Frown, TrendingUp, Sparkles, Heart, Loader2 } from 'lucide-react';
import { apiClient } from '../../api/client';

// Module-scoped tooltip renderer for the recharts <Tooltip content={…} />.
// Previously defined inline inside MoodTrackerCard's render, causing recharts
// to rebuild the tooltip on every state change (mood select, poll, hover).
const MoodChartTooltip = ({ active, payload }) => {
  if (active && payload && payload[0]) {
    const data = payload[0].payload;
    return (
      <div className="bg-white p-2 rounded-lg shadow-lg border text-sm">
        <p className="font-medium">{data.mood_label || 'Not logged'}</p>
        {data.score && <p className="text-gray-600">{data.score}%</p>}
      </div>
    );
  }
  return null;
};

// Pure — no closure state. Hoisted from inside the component so that
// `fetchMoodHistory` can safely reference it under `useCallback([])`
// without dragging it into the deps array on every render.
const generateFallbackData = () => {
  const moods = [
    { score: 0.7, label: 'Good' },
    { score: 0.5, label: 'Okay' },
    { score: 0.8, label: 'Great' },
    { score: 0.6, label: 'Good' },
    { score: 0.75, label: 'Good' },
    { score: 0.9, label: 'Amazing' },
    { score: null, label: null },
  ];

  return moods.map((mood, index) => {
    const date = new Date();
    date.setDate(date.getDate() - (6 - index));
    return {
      date: date.toISOString().split('T')[0],
      mood_score: mood.score,
      mood_label: mood.label,
    };
  });
};

const MoodTrackerCard = () => {
  const [moodData, setMoodData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMood, setSelectedMood] = useState(null);
  const [todayLogged, setTodayLogged] = useState(false);

  // useCallback Guard: `fetchMoodHistory` is only referenced by the
  // mount-effect below. All values it reads (React setters, the imported
  // `apiClient`, the now-module-scoped `generateFallbackData`) are stable,
  // so we can lock it with an empty deps array. This gives the effect a
  // stable function reference — no infinite loop, no double-fetch.
  const fetchMoodHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiClient.getMoodHistory(7);
      setMoodData(data);

      // Check if today is logged
      const today = new Date().toISOString().split('T')[0];
      const todayEntry = data.find(d => d.date === today);
      if (todayEntry && todayEntry.mood_score !== null) {
        setTodayLogged(true);
        setSelectedMood(todayEntry.mood_label);
      }
    } catch (err) {
      console.error('Failed to fetch mood history:', err);
      // Use generated fallback data
      const fallback = generateFallbackData();
      setMoodData(fallback);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMoodHistory();
  }, [fetchMoodHistory]);

  const logMood = async (score, label) => {
    try {
      setSelectedMood(label);
      await apiClient.logMood({ mood_score: score, mood_label: label });
      setTodayLogged(true);

      // Update local data
      const today = new Date().toISOString().split('T')[0];
      setMoodData(prev => prev.map(d =>
        d.date === today ? { ...d, mood_score: score, mood_label: label } : d
      ));
    } catch (err) {
      console.error('Failed to log mood:', err);
      // Still show as selected for better UX
      setTodayLogged(true);
    }
  };

  // Prepare chart data with day names
  const chartData = moodData.map(day => ({
    ...day,
    displayDate: new Date(day.date).toLocaleDateString('en-US', { weekday: 'short' }),
    score: day.mood_score ? Math.round(day.mood_score * 100) : null
  }));

  // Calculate trend
  const validScores = moodData.filter(d => d.mood_score !== null).map(d => d.mood_score);
  const avgScore = validScores.length > 0
    ? Math.round((validScores.reduce((a, b) => a + b, 0) / validScores.length) * 100)
    : 0;

  const recentScores = validScores.slice(-3);
  const olderScores = validScores.slice(0, -3);
  const trendUp = recentScores.length > 0 && olderScores.length > 0 &&
    (recentScores.reduce((a, b) => a + b, 0) / recentScores.length) >
    (olderScores.reduce((a, b) => a + b, 0) / olderScores.length);

  if (loading) {
    return (
      <div className="card" data-testid="mood-tracker-card">
        <div className="card-header">
          <h2 className="card-title">Mood Tracker</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="card" data-testid="mood-tracker-card">
      <div className="card-header">
        <h2 className="card-title">Mood Tracker</h2>
        {avgScore > 0 && (
          <div className="flex items-center gap-1 text-sm">
            <TrendingUp size={16} className={trendUp ? 'text-green-500' : 'text-gray-400'} />
            <span className="text-gray-600">{avgScore}% avg</span>
          </div>
        )}
      </div>

      {/* Chart — only show when there's data */}
      {validScores.length > 0 ? (
        <div className="h-32 mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis
              dataKey="displayDate"
              tick={{ fontSize: 10, fill: '#6B7280' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 10, fill: '#6B7280' }}
              axisLine={false}
              tickLine={false}
              width={30}
            />
            <Tooltip content={MoodChartTooltip} />
            <Line
              type="monotone"
              dataKey="score"
              stroke="#8B5CF6"
              strokeWidth={2}
              dot={{ fill: '#8B5CF6', strokeWidth: 0, r: 4 }}
              activeDot={{ r: 6, fill: '#7C3AED' }}
              connectNulls
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      ) : (
        <div className="text-center py-4 mb-3">
          <p className="text-gray-400 text-sm">Start logging to see your mood trends</p>
        </div>
      )}

      {/* Quick Mood Log - 5-Point Scale */}
      <div className="border-t border-gray-200 pt-4">
        <p className="text-sm font-medium text-gray-700 mb-3">
          {todayLogged ? `Today: ${selectedMood}` : 'How are you feeling today?'}
        </p>
        <div className="flex gap-2">
          {[
            { score: 1.0, label: 'Amazing', icon: Sparkles, color: 'purple', hoverBg: 'purple' },
            { score: 0.75, label: 'Good', icon: Smile, color: 'green', hoverBg: 'green' },
            { score: 0.5, label: 'Okay', icon: Meh, color: 'yellow', hoverBg: 'yellow' },
            { score: 0.25, label: 'Low', icon: Frown, color: 'orange', hoverBg: 'orange' },
            { score: 0.1, label: 'Struggling', icon: Heart, color: 'red', hoverBg: 'red' }
          ].map(({ score, label, icon: Icon, color, hoverBg }) => {
            const isSelected = selectedMood === label;
            return (
              <button
                key={label}
                onClick={() => !todayLogged && logMood(score, label)}
                disabled={todayLogged && !isSelected}
                className={`flex-1 py-2.5 px-2 rounded-lg border-2 transition-all flex flex-col items-center gap-1 ${
                  isSelected
                    ? `border-${color}-400 bg-${color}-50 ring-2 ring-${color}-300`
                    : todayLogged
                      ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                      : `border-gray-200 hover:border-${color}-400 hover:bg-${color}-50`
                }`}
                data-testid={`mood-${label.toLowerCase()}`}
              >
                <Icon
                  size={22}
                  className={`text-${color}-500`}
                  fill={isSelected ? 'currentColor' : 'none'}
                />
                <span className={`text-xs font-medium ${isSelected ? `text-${color}-700` : 'text-gray-700'}`}>
                  {label}
                </span>
              </button>
            );
          })}
        </div>
        {todayLogged && (
          <p className="text-xs text-center text-green-600 mt-2">
            ✓ Mood logged for today
          </p>
        )}
      </div>
    </div>
  );
};

export default MoodTrackerCard;
